import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { HierarchyFilterComponent } from '../../components/FilterBar/HierarchyFilter';
import { HierarchyFilter } from '../../lib/types';
import { subDays } from 'date-fns';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Activity, Package, TrendingUp, Users, Tv } from 'lucide-react';

interface DashboardOverviewData {
  operational: {
    totalMachines: number;
    liveMachines: number;
    offlineMachines: number;
    totalStockLevel: number;
  };
  stock: {
    criticalMachines: number;
    criticalProducts: number;
    avgStockLevel: number;
  };
  sales: {
    totalRevenue: number;
    totalUnits: number;
    avgTransactionValue: number;
    avgRateOfSale: number;
  };
  usage: {
    totalFootfall: number;
    avgCheckoutTime: number;
    conversionRate: number;
  };
  media: {
    activeCampaigns: number;
    totalImpressions: number;
    totalImpact: number;
  };
}

export function BrandDashboard() {
  const { brandId } = useParams<{ brandId: string }>();
  const [user, setUser] = useState<any>(null);
  const [filter, setFilter] = useState<HierarchyFilter>({
    dateFrom: subDays(new Date(), 30).toISOString(),
    dateTo: new Date().toISOString(),
    granularity: 'day',
    compareToPrevious: false,
    brandScope: 'sku_or_campaign',
  });

  const [data, setData] = useState<DashboardOverviewData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get user info to get brandId
    const token = localStorage.getItem('token');
    if (token) {
      fetch('/api/auth/me', {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      .then(res => res.json())
      .then(data => {
        setUser(data.user);
      });
    }
  }, []);

  useEffect(() => {
    if (user?.tenantId) {
      fetchOverviewData();
    }
  }, [user, filter]);

  const fetchOverviewData = async () => {
    try {
      setLoading(true);
      // Mock data for now - will be replaced with actual API calls
      const mockData: DashboardOverviewData = {
        operational: {
          totalMachines: 48,
          liveMachines: 45,
          offlineMachines: 3,
          totalStockLevel: 78.0
        },
        stock: {
          criticalMachines: 12,
          criticalProducts: 8,
          avgStockLevel: 78.0
        },
        sales: {
          totalRevenue: 12540.50,
          totalUnits: 2985,
          avgTransactionValue: 4.20,
          avgRateOfSale: 2.40
        },
        usage: {
          totalFootfall: 15680,
          avgCheckoutTime: 12.5,
          conversionRate: 19.1
        },
        media: {
          activeCampaigns: 5,
          totalImpressions: 45680,
          totalImpact: 2340.75
        }
      };
      
      setData(mockData);
    } catch (error) {
      console.error('Error fetching overview data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    console.log('Export CSV');
  };

  const handleExportPNG = () => {
    console.log('Export PNG');
  };

  const handleExportPagePDF = () => {
    console.log('Export Page PDF');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg text-red-600">Failed to load data</div>
      </div>
    );
  }

  const sections = [
    {
      id: 'operational',
      title: 'Operational Performance',
      description: 'Machine status, vend metrics, and stock levels',
      icon: Activity,
      link: `/brand/${brandId}/dashboard/operational`,
      metrics: [
        { label: 'Total Machines', value: data.operational.totalMachines },
        { label: 'Live Machines', value: data.operational.liveMachines },
        { label: 'Stock Level', value: `${data.operational.totalStockLevel}%` }
      ]
    },
    {
      id: 'stock',
      title: 'Stock Reporting',
      description: 'Stock levels over time and critical stock alerts',
      icon: Package,
      link: `/brand/${brandId}/dashboard/stock`,
      metrics: [
        { label: 'Critical Machines', value: data.stock.criticalMachines },
        { label: 'Critical Products', value: data.stock.criticalProducts },
        { label: 'Avg Stock Level', value: `${data.stock.avgStockLevel}%` }
      ]
    },
    {
      id: 'sales',
      title: 'Sales Performance',
      description: 'Revenue, units, and conversion analytics',
      icon: TrendingUp,
      link: `/brand/${brandId}/dashboard/sales`,
      metrics: [
        { label: 'Total Revenue', value: `£${data.sales.totalRevenue.toLocaleString()}` },
        { label: 'Total Units', value: data.sales.totalUnits.toLocaleString() },
        { label: 'Avg Transaction', value: `£${data.sales.avgTransactionValue}` }
      ]
    },
    {
      id: 'usage',
      title: 'Usage Analytics',
      description: 'Footfall, demographics, and user behavior',
      icon: Users,
      link: `/brand/${brandId}/dashboard/usage`,
      metrics: [
        { label: 'Total Footfall', value: data.usage.totalFootfall.toLocaleString() },
        { label: 'Conversion Rate', value: `${data.usage.conversionRate}%` },
        { label: 'Avg Checkout Time', value: `${data.usage.avgCheckoutTime}s` }
      ]
    },
    {
      id: 'media',
      title: 'Retail Media Performance',
      description: 'Campaign performance and media impact',
      icon: Tv,
      link: `/brand/${brandId}/dashboard/media`,
      metrics: [
        { label: 'Active Campaigns', value: data.media.activeCampaigns },
        { label: 'Total Impressions', value: data.media.totalImpressions.toLocaleString() },
        { label: 'Total Impact', value: `£${data.media.totalImpact.toLocaleString()}` }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Filter Section with proper separation */}
      <div className="bg-white shadow-sm">
        <HierarchyFilterComponent
          filter={filter}
          onChange={setFilter}
          onExportCSV={handleExportCSV}
          onExportPNG={handleExportPNG}
          onExportPagePDF={handleExportPagePDF}
          showBrandScope={true}
        />
      </div>

      {/* Main Content with proper spacing */}
      <div className="max-w-7xl mx-auto p-6 pt-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard Overview</h1>
          <p className="text-gray-600">Select a section to view detailed analytics and insights</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sections.map((section) => {
            const IconComponent = section.icon;
            return (
              <Link key={section.id} to={section.link}>
                <Card className="h-full hover:shadow-lg transition-shadow cursor-pointer">
                  <CardHeader className="pb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <IconComponent className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{section.title}</CardTitle>
                        <CardDescription className="text-sm">
                          {section.description}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {section.metrics.map((metric, index) => (
                        <div key={index} className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">{metric.label}</span>
                          <span className="text-sm font-semibold text-gray-900">{metric.value}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}